import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  Check,
  Database,
  Building2,
  Sparkles,
} from "lucide-react";
import { useTenantData } from "@/contexts/TenantModeContext";
import { cn } from "@/lib/utils";

/**
 * EnvironmentSwitcher — top-right header control.
 *
 * Allows operators to swap between Demo Mode (pre-populated demo tenants
 * registered in `DEMO_TENANT_REGISTRY`) and Customer Mode (empty tenant
 * with guided onboarding). Switching is instant — no page reload.
 *
 * Architecture: this control only mutates the data source via
 * TenantModeContext. The product UI, routes and workflow engine are
 * identical in both modes.
 */
export default function EnvironmentSwitcher() {
  const {
    mode,
    setMode,
    demoTenantId,
    setDemoTenantId,
    availableDemoTenants,
    isDemo,
  } = useTenantData();

  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const activeTenant = availableDemoTenants.find((t) => t.id === demoTenantId);

  const selectDemo = useCallback(
    (id) => {
      setDemoTenantId(id);
      setMode("demo");
      setOpen(false);
    },
    [setDemoTenantId, setMode],
  );

  const selectCustomer = useCallback(() => {
    setMode("customer");
    setOpen(false);
  }, [setMode]);

  return (
    <div className="relative" ref={ref}>
      <EnvSwitcherTrigger
        isDemo={isDemo}
        activeTenantLabel={activeTenant?.label}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />

      {open && (
        <EnvSwitcherMenu
          mode={mode}
          isDemo={isDemo}
          demoTenantId={demoTenantId}
          availableDemoTenants={availableDemoTenants}
          onSelectDemo={selectDemo}
          onSelectCustomer={selectCustomer}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Trigger button
// ───────────────────────────────────────────────────────────────────────────
function EnvSwitcherTrigger({ isDemo, activeTenantLabel, open, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "inline-flex items-center gap-2 h-9 pl-2 pr-2 rounded-md border transition-all",
        isDemo
          ? "bg-gold-300/10 border-gold-600/40 hover:bg-gold-300/20"
          : "bg-tralenex-50 border-tralenex-300 hover:bg-tralenex-200/60",
      )}
      data-testid="env-switcher-trigger"
      aria-haspopup="menu"
      aria-expanded={open}
      title={isDemo ? "Demo Mode — switch environment" : "Customer Mode — switch environment"}
    >
      {isDemo ? (
        <Sparkles size={13} className="text-gold-600 shrink-0" />
      ) : (
        <Building2 size={13} className="text-tralenex-700 shrink-0" />
      )}
      <span className="flex flex-col items-start leading-none">
        <span
          className={cn(
            "text-[9px] font-bold tracking-[0.12em] uppercase",
            isDemo ? "text-gold-600" : "text-tralenex-700",
          )}
          data-testid="env-switcher-mode-label"
        >
          {isDemo ? "Demo" : "Customer"}
        </span>
        <span className="text-[11px] font-semibold text-ink-primary mt-0.5 max-w-[140px] truncate">
          {isDemo ? activeTenantLabel || "Demo tenant" : "Your Organisation"}
        </span>
      </span>
      <ChevronDown size={12} className="text-ink-secondary shrink-0 ml-0.5" />
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Dropdown menu container
// ───────────────────────────────────────────────────────────────────────────
function EnvSwitcherMenu({
  isDemo,
  demoTenantId,
  availableDemoTenants,
  onSelectDemo,
  onSelectCustomer,
}) {
  return (
    <div
      className="absolute right-0 top-full mt-2 w-[340px] rounded-md border border-border-subtle bg-raised shadow-e3 anim-fade-up z-50"
      data-testid="env-switcher-menu"
      role="menu"
    >
      <div className="px-3 py-2 border-b border-border-subtle">
        <p className="eyebrow">Environment</p>
        <p className="text-meta text-ink-secondary mt-0.5">
          Same product · same screens · only the data source changes.
        </p>
      </div>

      <DemoModeGroup
        tenants={availableDemoTenants}
        activeTenantId={isDemo ? demoTenantId : null}
        onSelect={onSelectDemo}
      />

      <div className="border-t border-border-subtle" />

      <CustomerModeOption isActive={!isDemo} onSelect={onSelectCustomer} />

      <div className="border-t border-border-subtle px-3 py-2 bg-inset/40">
        <p className="text-[10px] text-ink-disabled leading-relaxed">
          Single application · single workflow engine. The architecture supports
          additional demo tenants and production tenants without redesign.
        </p>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Demo tenant group
// ───────────────────────────────────────────────────────────────────────────
function DemoModeGroup({ tenants, activeTenantId, onSelect }) {
  return (
    <>
      <div className="px-3 pt-3 pb-1">
        <p className="text-[10px] font-bold tracking-[0.1em] uppercase text-gold-600 flex items-center gap-1.5">
          <Sparkles size={11} /> Demo Mode
        </p>
        <p className="text-[11px] text-ink-secondary mt-0.5">
          For demonstrations, screenshots and design partner sessions.
        </p>
      </div>
      <ul className="pb-1">
        {tenants.map((t) => (
          <DemoTenantOption
            key={t.id}
            tenant={t}
            active={activeTenantId === t.id}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </>
  );
}

function DemoTenantOption({ tenant, active, onSelect }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(tenant.id)}
        data-testid={`env-switcher-demo-${tenant.id}`}
        className={cn(
          "w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-inset transition-colors",
          active && "bg-gold-300/10",
        )}
        role="menuitemradio"
        aria-checked={active}
      >
        <Database
          size={15}
          className={cn("mt-0.5 shrink-0", active ? "text-gold-600" : "text-ink-secondary")}
        />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-ink-primary truncate">
            {tenant.label}
          </span>
          <span className="block text-meta text-ink-secondary truncate">
            {tenant.subtitle}
          </span>
        </span>
        {active && <Check size={14} className="text-gold-600 mt-1 shrink-0" />}
      </button>
    </li>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Customer mode option
// ───────────────────────────────────────────────────────────────────────────
function CustomerModeOption({ isActive, onSelect }) {
  return (
    <>
      <div className="px-3 pt-3 pb-1">
        <p className="text-[10px] font-bold tracking-[0.1em] uppercase text-tralenex-700 flex items-center gap-1.5">
          <Building2 size={11} /> Customer Mode
        </p>
        <p className="text-[11px] text-ink-secondary mt-0.5">
          For real customer usage — empty tenant + guided onboarding.
        </p>
      </div>
      <ul className="pb-2">
        <li>
          <button
            type="button"
            onClick={onSelect}
            data-testid="env-switcher-customer"
            className={cn(
              "w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-inset transition-colors",
              isActive && "bg-tralenex-50",
            )}
            role="menuitemradio"
            aria-checked={isActive}
          >
            <Building2
              size={15}
              className={cn(
                "mt-0.5 shrink-0",
                isActive ? "text-tralenex-700" : "text-ink-secondary",
              )}
            />
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold text-ink-primary">
                Your Organisation
              </span>
              <span className="block text-meta text-ink-secondary">
                Empty workspace · setup wizard · first-use experience
              </span>
            </span>
            {isActive && <Check size={14} className="text-tralenex-700 mt-1 shrink-0" />}
          </button>
        </li>
      </ul>
    </>
  );
}
